/**
 * Shared fee model utilities for parsing, formatting, and displaying fee models
 * across ApplicationDetails and InviteDetails pages.
 */

/**
 * Parse a fee models value (string or array) into a normalized array of { type, rate } objects.
 * @param {string|Array} feeModelsValue - Raw fee models (JSON string or array)
 * @returns {Array<{type: string, rate: number}>}
 */
export const parseFeeModels = (feeModelsValue) => {
  if (!feeModelsValue) return [];

  let parsed = feeModelsValue;

  // If it's a JSON string, parse it first
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      return [];
    }
  }

  // If it's an array, normalize it
  if (Array.isArray(parsed) && parsed.length > 0) {
    return parsed.map((fm) => {
      const entry = {
        type: fm.type || fm.fee_model,
        rate: parseFloat(fm.rate || fm.starting_rate) || 0,
      };
      const hours = Number.parseFloat(fm.hours);
      if (Number.isFinite(hours) && hours > 0) {
        entry.hours = hours;
      }
      return entry;
    }).filter((fm) => fm.type && !Number.isNaN(fm.rate));
  }

  return [];
};

/**
 * Get fee models from collaboration/application data, with optional override for negotiated models.
 * For invites: checks negotiatedFeeModels override > collab.fee_models > fallback old format
 * For applications: checks proposed_fee_models > negotiated_fee_models > fee_models > fallback old format
 *
 * @param {Object} collab - The collaboration/application data object
 * @param {string|Array|null} negotiatedFeeModels - Optional override (for negotiation display)
 * @returns {Array<{type: string, rate: number}>}
 */
export const getFeeModels = (collab, negotiatedFeeModels = null) => {
  // If negotiated fee models override is provided, use those
  if (negotiatedFeeModels !== null && negotiatedFeeModels !== undefined) {
    const parsed = parseFeeModels(negotiatedFeeModels);
    if (parsed.length > 0) return parsed;
  }

  // Negotiated terms on the object (invite row, merged application, etc.) beat listing fee_models
  if (collab?.negotiated_fee_models !== undefined && collab?.negotiated_fee_models !== null) {
    const parsed = parseFeeModels(collab.negotiated_fee_models);
    if (parsed.length > 0) return parsed;
  }

  // Support new format (fee_models array) from campaign
  if (collab?.fee_models !== undefined && collab?.fee_models !== null) {
    const parsed = parseFeeModels(collab.fee_models);
    if (parsed.length > 0) return parsed;
  }

  // Fallback to old format for backward compatibility
  if (collab?.fee_model && collab.starting_rate !== undefined && collab.starting_rate !== null) {
    return [{ type: collab.fee_model, rate: parseFloat(collab.starting_rate) }];
  }

  return [];
};

/**
 * Get fee models for application details (checks proposed, negotiated, then campaign fee_models).
 *
 * @param {Object} app - The application data object
 * @returns {Array<{type: string, rate: number}>}
 */
export const getApplicationFeeModels = (app) => {
  if (app?.proposed_fee_models) {
    const parsed = parseFeeModels(app.proposed_fee_models);
    if (parsed.length > 0) return parsed;
  }

  if (app?.negotiated_fee_models) {
    const parsed = parseFeeModels(app.negotiated_fee_models);
    if (parsed.length > 0) return parsed;
  }

  // Fallback to campaign fee_models
  if (app?.fee_models) {
    const parsed = parseFeeModels(app.fee_models);
    if (parsed.length > 0) return parsed;
  }

  return [];
};

/**
 * Format a single fee model for display.
 * @param {Object} feeModel - { type, rate }
 * @returns {string}
 */
export const formatFeeModelDisplay = (feeModel) => {
  if (feeModel.type === 'commission' || feeModel.type === 'commission_rate') {
    return `${feeModel.rate}% Commission`;
  }
  if (feeModel.type === 'hourly' || feeModel.type === 'hourly_rate') {
    return `$${feeModel.rate}/hr`;
  }
  if (feeModel.type === 'flat' || feeModel.type === 'flat_rate') {
    return `$${feeModel.rate} Flat Rate`;
  }
  return `${feeModel.rate} ${feeModel.type}`;
};

/**
 * Format one or more fee models as a single readable line.
 * @param {Array<{type: string, rate: number}>|string|null|undefined} feeModelsInput - Normalized array or JSON/array payload
 * @param {string} [separator=' · '] - Between models
 * @returns {string}
 */
export const formatFeeModelsSummary = (feeModelsInput, separator = ' · ') => {
  let models;
  if (Array.isArray(feeModelsInput) && feeModelsInput.length > 0) {
    models = feeModelsInput;
  } else {
    models = parseFeeModels(feeModelsInput);
  }
  if (!models.length) return 'N/A';
  return models.map((m) => formatFeeModelDisplay(m)).join(separator);
};

/**
 * Format a fee model type string for display (e.g., "flat_rate" -> "Flat Rate").
 * @param {string} model - The fee model type string
 * @returns {string}
 */
export const formatFeeModel = (model) => {
  if (!model) return 'N/A';
  return model.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

/**
 * Short column label for offer cards (e.g. flat_rate -> "Flat rate").
 * @param {string} type - Fee model type
 * @returns {string}
 */
export const getFeeModelColumnLabel = (type) => {
  if (type === 'commission' || type === 'commission_rate') return 'Commission';
  if (type === 'hourly' || type === 'hourly_rate') return 'Hourly rate';
  if (type === 'flat' || type === 'flat_rate') return 'Flat rate';
  return formatFeeModel(type);
};

/**
 * Large value for offer card columns (e.g. "$345", "44%").
 * @param {{ type: string, rate: number|string }} feeModel
 * @returns {string}
 */
export const formatFeeModelColumnValue = (feeModel) => {
  if (!feeModel) return 'N/A';
  const { type, rate } = feeModel;
  if (rate == null || rate === '') return 'N/A';
  if (type === 'commission' || type === 'commission_rate') return `${rate}%`;
  if (type === 'hourly' || type === 'hourly_rate') return `$${rate}/hr`;
  if (type === 'flat' || type === 'flat_rate') return `$${rate}`;
  return String(rate);
};

/**
 * Format compensation based on fee model type and rate.
 * @param {Object} collab - Collaboration/application object with fee_model, starting_rate
 * @param {number|string|null} rate - Rate to format (falls back to collab.starting_rate)
 * @returns {string}
 */
export const formatCompensation = (collab, rate) => {
  if (!collab) return 'N/A';
  const models = parseFeeModels(collab.fee_models);
  if (models.length > 1) {
    return formatFeeModelsSummary(models);
  }
  const primary = models[0];
  const feeModel = collab.fee_model || primary?.type;
  const hasExplicitRate = rate !== undefined && rate !== null && rate !== '';
  let rateToFormat;
  if (hasExplicitRate) {
    rateToFormat = rate;
  } else if (collab.starting_rate !== undefined && collab.starting_rate !== null && collab.starting_rate !== '') {
    rateToFormat = collab.starting_rate;
  } else {
    rateToFormat = primary?.rate;
  }
  if (feeModel === 'flat_rate' || feeModel === 'flat') {
    return rateToFormat != null && rateToFormat !== '' ? `$${rateToFormat} USD` : 'N/A';
  }
  if (feeModel === 'commission' || feeModel === 'commission_rate') {
    return rateToFormat != null && rateToFormat !== '' ? `${rateToFormat}%` : 'N/A';
  }
  if (feeModel === 'hourly_rate' || feeModel === 'hourly') {
    return rateToFormat != null && rateToFormat !== '' ? `$${rateToFormat}/hr` : 'N/A';
  }
  if (rateToFormat != null && rateToFormat !== '') {
    return String(rateToFormat);
  }
  return 'N/A';
};

/**
 * Format a rate as a detailed string based on fee_model type (for negotiation displays).
 * @param {number} rate - The rate value
 * @param {string} feeModel - The fee model type
 * @returns {string}
 */
export const formatRateByFeeModel = (rate, feeModel) => {
  if (feeModel === 'flat_rate' || feeModel === 'flat') {
    return `$${Number(rate).toFixed(2)} flat rate`;
  }
  if (feeModel === 'hourly_rate' || feeModel === 'hourly') {
    return `$${Number(rate).toFixed(2)}/hr`;
  }
  if (feeModel === 'commission_rate' || feeModel === 'commission') {
    return `${Number(rate).toFixed(1)}% commission`;
  }
  return `$${rate}`;
};
